<script setup lang="ts">
import DecorTitle from "@/components/uikit/DecorTitle.vue";
import UserPoints from "@/components/uikit/UserPoints.vue";
import GreenButton from "@/components/uikit/GreenButton.vue";
import { useRouter } from "vue-router";
import { useGameStore } from "@/stores/index";
import { ref, computed, onMounted, watch } from "vue";
import { Swiper, SwiperSlide } from "swiper/vue";
import "swiper/css";
import "swiper/css/grid";
import "swiper/css/pagination";
import "swiper/css/effect-fade";
import { Grid, Pagination, Navigation } from "swiper/modules";
import { getImagePath } from "@/utils/image";

interface World {
  id: number;
  name: string;
}

const modules = [Grid, Pagination, Navigation];
const store = useGameStore();
const router = useRouter();
const selectedItems = ref<World[]>([]);
const maxSelection = 1;
const videoUrl = ref("");
const worldsModal = ref(false);

const pagination = {
  el: ".worlds-pagination",
  clickable: true,
};
const navigation = {
  nextEl: ".worlds .slider-navigation__btn--next",
  prevEl: ".worlds .slider-navigation__btn--prev",
};

// Переключение активности
const toggleSelection = (item: World) => {
  const index = selectedItems.value.findIndex(
    (selected) => selected.id === item.id
  );

  if (index !== -1) {
    selectedItems.value.splice(index, 1);
  } else if (selectedItems.value.length < maxSelection) {
    selectedItems.value.push(item);
  }
};

// Выбор мира
const selectWorlds = async () => {
  if (selectedItems.value.length === 1) {
    const ids = selectedItems.value.map((item) => Number(item.id));

    await store.selectWorlds(ids[0]);
    router.push("/mission");
  }
};

const selectVideo = (video: string) => {
  videoUrl.value = video;
  worldsModal.value = true;
};

const closeModal = () => {
  videoUrl.value = "";
  worldsModal.value = false;
};

watch(
  () => store.playerInfoArray?.selectedWorlds,
  (newValue) => {
    if (newValue) {
      selectedItems.value = newValue;
    }
  },
  { immediate: true }
);

onMounted(() => {
  store.getWorlds();
});
</script>

<template>
  <section class="worlds">
    <div class="worlds__bg">
      <img src="/images/worlds-bg.jpg" alt="" />
    </div>
    <div class="worlds__top">
      <DecorTitle
        :title="'Выбери мир для путешествий'"
        :icon="'/images/worlds-icon-top.svg'"
      />
      <UserPoints :count1="store.playerScore" />
    </div>
    <div class="worlds__slider">
      <swiper
        :slidesPerView="3"
        :grid="{
          rows: 4,
          fill: 'row',
        }"
        :spaceBetween="30"
        :slidesPerGroup="3"
        :modules="modules"
        :pagination="pagination"
        :navigation="navigation"
      >
        <swiper-slide v-for="(item, i) in store.worldsList" :key="i">
          <div
            class="worlds__item-icon"
            @click="selectVideo(item.videoUrl)"
          ></div>
          <div
            class="worlds__item"
            :class="{
              'worlds__item--active': selectedItems.some(
                (selected) => selected.id === item.id
              ),
            }"
            @click="toggleSelection(item)"
          >
            <div class="worlds__item-wrap">{{ item.name }}</div>
          </div>
        </swiper-slide>
      </swiper>
      <div class="slider-navigation">
        <div class="slider-navigation__btn slider-navigation__btn--prev"></div>
        <div class="slider-navigation__btn slider-navigation__btn--next"></div>
      </div>
      <div class="slider-pagination worlds-pagination"></div>
    </div>
    <div class="worlds__bottom">
      <GreenButton v-if="selectedItems.length === 1" @click="selectWorlds"
        >Далее</GreenButton
      >
    </div>
  </section>
  <div class="worlds-modal" :class="{ 'worlds-modal--active': worldsModal }">
    <div class="worlds-modal__wrap">
      <div class="worlds-modal__back" @click="closeModal">Вернуться</div>
      <div class="worlds-modal__video">
        <video :src="getImagePath(videoUrl)" controls></video>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.worlds {
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: relative;
  &__bg {
    position: absolute;
    width: 100%;
    height: 100%;
    z-index: -1;
    & img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
  &__top {
    padding: 40px 60px 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  &__slider {
    padding: 0 125px;
    // height: 550px;
    position: relative;

    & .swiper-slide {
      height: calc((100% - 24px) / 4) !important;
      position: relative;
    }
  }
  &__item {
    position: relative;
    cursor: pointer;
    &-icon {
      cursor: pointer;
      width: 24px;
      height: 24px;
      z-index: 3;
      position: absolute;
      top: 12px;
      left: 24px;
      opacity: 0.6;
      background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg clip-path='url(%23clip0_1084_2429)'%3E%3Cpath d='M20 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM9.5 16.5V7.5L16.5 12L9.5 16.5Z' fill='white' fill-opacity='1'/%3E%3C/g%3E%3Cdefs%3E%3CclipPath id='clip0_1084_2429'%3E%3Crect width='24' height='24' fill='white'/%3E%3C/clipPath%3E%3C/defs%3E%3C/svg%3E%0A");
      transition: opacity var(--time);
      &:hover {
        opacity: 1;
      }
    }
    &-wrap {
      min-height: 114px;
      padding: 24px;
      background: var(--bg-transparent);
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: 18px;
      line-height: 100%;
      font-weight: 600;
      color: var(--white);
      text-transform: uppercase;
      text-align: center;
      font-family: "Tektur", sans-serif;
      border: 1px solid var(--gray);
      clip-path: polygon(
        0 0,
        calc(100% - 20px) 0,
        100% 20px,
        100% 100%,
        20px 100%,
        0 calc(100% - 20px)
      );
      transition: color var(--time), border var(--time);
    }
    &::after {
      position: absolute;
      content: "";
      width: 28.1px;
      height: 1px;
      left: -4px;
      transform: rotate(45deg);
      background: var(--gray);
      bottom: 10px;
      z-index: 2;
      transition: background var(--time);
    }
    &::before {
      position: absolute;
      content: "";
      width: 28.1px;
      height: 1px;
      right: -4px;
      transform: rotate(45deg);
      background: var(--gray);
      top: 10px;
      z-index: 2;
      transition: background var(--time);
    }
    &--active {
      & .worlds__item-wrap {
        border: 1px solid var(--green);
        color: var(--green);
      }
      &::after {
        background: var(--green);
      }
      &::before {
        background: var(--green);
      }
    }
  }
  &__bottom {
    padding: 0 60px 40px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    min-height: 90px;
  }
  &__progress {
    width: 262px;
    height: 40px;
    position: relative;
    &-current {
      position: absolute;
      height: 100%;
      width: 100%;
      top: 0;
      left: 0;
    }
  }
}

.worlds-modal {
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 111;
  background: rgba(224, 244, 244, 0.2);
  backdrop-filter: blur(75px);
  padding: 100px 0;
  opacity: 0;
  visibility: hidden;
  transition: opacity var(--time), visibility var(--time);
  &--active {
    opacity: 1;
    visibility: visible;
  }
  &__wrap {
    width: 100%;
    height: 100%;
    border-top: 1px solid #36cdeb;
    border-bottom: 1px solid #36cdeb;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
  }
  &__back {
    display: flex;
    align-items: center;
    gap: 16px;
    font-size: 20px;
    line-height: 25px;
    font-weight: normal;
    color: rgba(255, 255, 255);
    cursor: pointer;
    text-transform: uppercase;
    font-family: "Tektur", sans-serif;
    position: absolute;
    top: 30px;
    left: 40px;
    z-index: 2;
    opacity: 0.2;
    transition: opacity var(--time);
    &::before {
      position: relative;
      content: "";
      width: 36px;
      height: 32px;
      flex-shrink: 0;
      background-image: url("data:image/svg+xml,%3Csvg width='36' height='32' viewBox='0 0 36 32' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath opacity='1' d='M34.9393 15.9708H0.998187M0.998187 15.9708L16.0831 0.885898M0.998187 15.9708L16.0831 31.0558' stroke='white' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A");
    }
    &:hover {
      opacity: 1;
    }
  }
  &__video {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    & video {
      max-width: 100%;
      max-height: 100%;
    }
  }
}
</style>
